import { useState } from 'react';
import { Card, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaLock, FaSave } from 'react-icons/fa';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';

const AdminProfile = () => {
  const { user, changePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileUpdate = (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Aquí iría la lógica para actualizar el perfil
      // await updateProfile(profileData);

      setAlert({
        show: true,
        message: 'Perfil actualizado correctamente',
        variant: 'success'
      });

      // Limpiar campos de contraseña
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

    } catch (error) {
      setAlert({
        show: true,
        message: 'Error al actualizar el perfil: ' + error.message,
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!profileData.currentPassword) {
      setAlert({
        show: true,
        message: 'Introduce tu contraseña actual',
        variant: 'danger'
      });
      return;
    }

    if (profileData.newPassword !== profileData.confirmPassword) {
      setAlert({
        show: true,
        message: 'Las contraseñas no coinciden',
        variant: 'danger'
      });
      return;
    }

    if (profileData.newPassword.length < 6) {
      setAlert({
        show: true,
        message: 'La contraseña debe tener al menos 6 caracteres',
        variant: 'danger'
      });
      return;
    }

    setLoading(true);

    try {
      await changePassword({
        currentPassword: profileData.currentPassword,
        newPassword: profileData.newPassword
      });

      setAlert({
        show: true,
        message: 'Contraseña cambiada correctamente',
        variant: 'success'
      });

      // Limpiar campos de contraseña
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

    } catch (error) {
      setAlert({
        show: true,
        message: error.response?.data?.message || error.message || 'Error al cambiar la contraseña',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Mi Perfil" subtitle="Gestiona tu información personal y configuración de cuenta">
      {alert.show && (
        <Alert 
          variant={alert.variant} 
          onClose={() => setAlert({ show: false, message: '', variant: 'success' })} 
          dismissible
          className="mb-4"
        >
          {alert.message}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          {/* Información Personal */}
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FaUser className="me-2 text-primary" />
                Información Personal
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleProfileUpdate}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nombre Completo</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={profileData.name}
                        onChange={handleInputChange}
                        placeholder="Ingresa tu nombre completo"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={profileData.email}
                        onChange={handleInputChange}
                        placeholder="tu@email.com"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Teléfono</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={profileData.phone}
                        onChange={handleInputChange}
                        placeholder="+34 600 000 000"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Rol</Form.Label>
                      <Form.Control
                        type="text"
                        value={user?.role || 'Administrador'}
                        disabled
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loading}
                    className="d-flex align-items-center"
                  >
                    <FaSave className="me-2" />
                    {loading ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Cambiar Contraseña */}
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FaLock className="me-2 text-warning" />
                Cambiar Contraseña
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handlePasswordChange}>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contraseña Actual</Form.Label>
                      <Form.Control
                        type="password"
                        name="currentPassword"
                        value={profileData.currentPassword}
                        onChange={handleInputChange}
                        placeholder="Ingresa tu contraseña actual"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nueva Contraseña</Form.Label>
                      <Form.Control
                        type="password"
                        name="newPassword"
                        value={profileData.newPassword}
                        onChange={handleInputChange}
                        placeholder="Mínimo 6 caracteres"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Confirmar Nueva Contraseña</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={profileData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Repite la nueva contraseña"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button 
                    type="submit" 
                    variant="warning" 
                    disabled={loading || !profileData.currentPassword || !profileData.newPassword}
                    className="d-flex align-items-center"
                  >
                    <FaLock className="me-2" />
                    {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Información de la Cuenta */}
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FaEnvelope className="me-2 text-info" />
                Información de la Cuenta
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-3">
                <div className="user-avatar-large mx-auto mb-3">
                  {(user?.name || 'A').charAt(0).toUpperCase()}
                </div>
                <h5>{user?.name || 'Administrador'}</h5>
                <p className="text-muted">{user?.email}</p>
              </div>
              
              <hr />
              
              <div className="mb-2">
                <strong>Rol:</strong> 
                <span className="ms-2 badge bg-primary">{user?.role || 'admin'}</span>
              </div>
              
              <div className="mb-2">
                <strong>Estado:</strong> 
                <span className="ms-2 badge bg-success">Activo</span>
              </div>
              
              <div className="mb-2">
                <strong>Último acceso:</strong>
                <p className="text-muted small mb-0">
                  {new Date().toLocaleDateString('es-ES')}
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </AdminLayout>
  );
};

export default AdminProfile;
